/**
 * Always-visible impersonation banner + viewport frame (SUPPORT-
 * IMPERSONATION). Mounted app-wide (a sibling of <App/> in main.tsx) so
 * it shows on every route while acting-as. A thick amber frame around the
 * whole viewport is the unmissable "you are not yourself" signal; a pill
 * at top-center names the chain and offers one-click Exit (pops one level).
 */
import { useState } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LogoutIcon from '@mui/icons-material/Logout';

import { useImpersonation } from '../auth/ImpersonationContext';

const AMBER = '#b45309';
const AMBER_FG = '#ffffff';

function roleLabel(role: 'tenant_admin' | 'participant'): string {
  return role === 'tenant_admin' ? 'Tenant admin' : 'Participant';
}

export function ImpersonationBanner() {
  const { chain, isImpersonating, readOnly, exit } = useImpersonation();
  const [exiting, setExiting] = useState(false);

  if (!isImpersonating) return null;

  const handleExit = async () => {
    setExiting(true);
    try {
      await exit();
    } finally {
      setExiting(false);
    }
  };

  const tip = chain[chain.length - 1];

  return (
    <>
      {/* Viewport frame — the ambient signal. Non-interactive. */}
      <Box
        aria-hidden
        sx={{
          position: 'fixed',
          inset: 0,
          border: `4px solid ${AMBER}`,
          pointerEvents: 'none',
          zIndex: 2000,
        }}
      />

      {/* Control pill — the chain + Exit. */}
      <Box
        role="status"
        sx={{
          position: 'fixed',
          top: 8,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 2001,
          bgcolor: AMBER,
          color: AMBER_FG,
          borderRadius: 999,
          boxShadow: 4,
          px: 1.75,
          py: 0.5,
          maxWidth: '92vw',
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <VisibilityIcon fontSize="small" />
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            Support session
          </Typography>
          <Typography
            variant="body2"
            sx={{ opacity: 0.95, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            title={chain.map((l) => `${roleLabel(l.role)}: ${l.label}`).join(' → ')}
          >
            Platform admin
            {chain.map((l, i) => (
              <span key={i}>
                {' → '}
                {roleLabel(l.role)} · {l.label}
              </span>
            ))}
          </Typography>
          {readOnly && (
            <Chip
              label="read-only"
              size="small"
              sx={{
                bgcolor: 'rgba(255,255,255,0.22)',
                color: AMBER_FG,
                fontWeight: 700,
                height: 20,
              }}
            />
          )}
          <Button
            size="small"
            variant="contained"
            startIcon={<LogoutIcon />}
            onClick={() => void handleExit()}
            disabled={exiting}
            sx={{
              bgcolor: '#ffffff',
              color: AMBER,
              fontWeight: 700,
              '&:hover': { bgcolor: '#f3f4f6' },
              textTransform: 'none',
              py: 0.25,
            }}
          >
            {exiting
              ? 'Exiting…'
              : chain.length > 1
                ? `Exit ${roleLabel(tip.role).toLowerCase()}`
                : 'Exit session'}
          </Button>
        </Stack>
      </Box>
    </>
  );
}
